import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { LobSplitDTO } from "../../../api/admin/types";

interface LobSplitPieProps {
  split: LobSplitDTO;
}

// Stable color per LOB slice. Unknown is grey so it visually recedes.
const SLICE_COLORS: Record<keyof LobSplitDTO, string> = {
  commercial: "#3949ab",
  personal: "#26a69a",
  employee_benefits: "#fb8c00",
  unknown: "#9ca3af",
};

const SLICE_LABELS: Record<keyof LobSplitDTO, string> = {
  commercial: "Commercial Lines",
  personal: "Personal Lines",
  employee_benefits: "Employee Benefits",
  unknown: "Unknown",
};

const LobSplitPie: React.FC<LobSplitPieProps> = ({ split }) => {
  const data = (Object.keys(SLICE_LABELS) as Array<keyof LobSplitDTO>)
    .map((k) => ({
      key: k,
      name: SLICE_LABELS[k],
      value: split[k] ?? 0,
      color: SLICE_COLORS[k],
    }))
    .filter((d) => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Line of business split</div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          Derived from is_bop / is_eb / is_farm flags + coverage_lines fallback
        </div>
      </div>
      {data.length === 0 ? (
        <div style={{ padding: 24, color: "#6b7280", textAlign: "center" }}>
          No policy uploads in this window.
        </div>
      ) : (
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <PieChart>
              <Tooltip
                formatter={(value: number, _name, item) => {
                  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                  return [`${value} (${pct}%)`, item.payload?.name];
                }}
              />
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                isAnimationActive={false}
                label={(entry) => {
                  const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                  return pct >= 5 ? `${pct}%` : "";
                }}
              >
                {data.map((d) => (
                  <Cell key={d.key} fill={d.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8 }}>
            {data.map((d) => (
              <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    background: d.color,
                    borderRadius: 2,
                  }}
                />
                <span style={{ color: "#374151" }}>
                  {d.name}: <b>{d.value}</b>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LobSplitPie;
